import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import type { InferType } from 'yup';
import { CLIENT_CREATE_QUERY_KEY } from '@/app/(main)/clients/create/const/const';
import {
  SEAT_PREFERENCES,
  type ClientPreferenceData,
  type CreateClientFormData,
  type SeatPreference,
} from '@/types/clientsApiTypes';
import { nullableString } from '@/utils/validations';
import { useUpdateClientPreferencesMutation } from '../../../hooks/useUpdateClientPreferencesMutation';
import { ModalKeys } from '../../EditSectionModals/types';
import { defineSection } from '../../EditSectionModals/utils/defineSection';
import { EditAllergiesForm } from './EditAllergiesForm';
import type { AllergiesFormValues } from './types';

export const ALLERGIES_MAX_LENGTH = 300;
const schema = yup.object({
  allergies: nullableString()
    .defined()
    .max(ALLERGIES_MAX_LENGTH, 'Allergies & dietary restrictions must not exceed 300 characters'),
  fly: yup
    .object({
      seat: yup.mixed<SeatPreference>().oneOf(Object.values(SEAT_PREFERENCES)).nullable().default(null),
    })
    .nullable()
    .default(null)
    .defined(),
});

function useAllergiesOnSave() {
  const mutation = useUpdateClientPreferencesMutation();
  const queryClient = useQueryClient();

  return async (payload: { preference_data: Partial<ClientPreferenceData> }) => {
    await mutation.mutateAsync(payload.preference_data, {
      onSuccess: () => {
        toast.success('Preferences updated successfully');
        queryClient.invalidateQueries({ queryKey: [CLIENT_CREATE_QUERY_KEY] });
      },
    });
  };
}

function mapClientToValues(client: CreateClientFormData): AllergiesFormValues {
  const pref = client?.primary_passenger?.preference_data;
  const allergies = typeof pref?.allergies === 'string' ? pref.allergies : (pref?.allergies ?? '') || '';
  return {
    allergies,
    fly: {
      seat: pref?.fly?.seat ?? null,
      preferred_private_aviation_company: pref?.fly?.preferred_private_aviation_company ?? null,
      fly_private_aviation: pref?.fly?.fly_private_aviation ?? null,
      class_preference: pref?.fly?.class_preference ?? null,
    },
  };
}

function mapValuesToPayload(values: InferType<typeof schema>): { preference_data: Partial<ClientPreferenceData> } {
  return {
    preference_data: {
      allergies: values.allergies ?? '',
    },
  };
}

const FieldsPrimary = () => <EditAllergiesForm isRelated={false} />;

export const allergiesSection = defineSection<
  (typeof ModalKeys)['allergies'],
  CreateClientFormData,
  typeof schema,
  { preference_data: Partial<ClientPreferenceData> }
>({
  key: ModalKeys.allergies,
  title: 'Allergies & dietary restrictions',
  subtitle: 'Health-related allergies and dietary considerations to account for during travel planning.',
  schema,
  mapClientToValues,
  mapValuesToPayload,
  useOnSave: useAllergiesOnSave,
  Fields: FieldsPrimary,
});